import { useEffect, useRef, useState } from 'react'
import { Search, Wand2, SlidersHorizontal, Route as RouteIcon } from 'lucide-react'
import { useRoadStore, filterRoads } from '@/lib/roads/store'
import { getStoredUserLocation, mapInstance, INITIAL_ZOOM } from '@/lib/roads/userLocation'
import { suppressNextMapClick } from '@/lib/roads/mapClickGate'
import AdminPortal from '@/components/panels/AdminPortal'
import AccountPanel from '@/components/panels/AccountPanel'
import AuthModal from '@/components/panels/AuthModal'
import UsernamePrompt from '@/components/panels/UsernamePrompt'
import { useAuthUi } from '@/lib/auth/authUi'
import UnderConstruction from '@/components/ui/UnderConstruction'
import { WELCOME_OPEN_EVENT } from '@/components/panels/WelcomePopup'

function YourRoutesButton({ className = '' }: { className?: string }) {
  const openRoutes = useAuthUi((s) => s.openRoutes)
  return (
    <button
      data-routes-toggle
      onClick={openRoutes}
      className={`h-11 px-3 md:px-4 rounded-lg bg-black/70 border-2 border-white backdrop-blur hover:border-amber-400 transition-colors flex items-center gap-2 text-sm text-white/80 ${className}`}
      title="Your saved routes"
    >
      <RouteIcon size={16} className="text-amber-400" />
      <span className="uppercase tracking-wider text-xs">Your Routes</span>
    </button>
  )
}

interface TopBarProps {
  onFindRide: () => void
  onToggleFilters: () => void
  active: boolean
}

function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <img
        src="/images/roadratings-logo-2.png"
        alt="RoadRatings"
        className="h-7 w-7 shrink-0 object-contain"
      />
      <div className="font-bold tracking-widest text-[11px] md:text-[13px] select-none">
        <span className="text-white">ROAD</span>
        <span className="text-amber-400">RATINGS</span>
      </div>
    </div>
  )
}

function FreshnessHint() {
  const meta = useRoadStore((s) => s.meta)
  const anyMeta = meta as unknown as Record<string, unknown> | null
  const stale = Boolean(anyMeta?.stale)
  const syncedAt = (anyMeta?.synced_at ?? anyMeta?.updated_at) as string | null | undefined

  if (!anyMeta) return null

  let rel = 'just now'
  if (syncedAt) {
    const diff = Date.now() - new Date(syncedAt).getTime()
    if (Number.isFinite(diff)) {
      const mins = Math.max(0, Math.floor(diff / 60000))
      if (mins < 1) rel = 'just now'
      else if (mins < 60) rel = `${mins}m ago`
      else if (mins < 1440) rel = `${Math.floor(mins / 60)}h ago`
      else rel = `${Math.floor(mins / 1440)}d ago`
    }
  }

  return (
    <div
      className={`hidden xl:flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider ${
        stale ? 'text-amber-400/90' : 'text-white/35'
      }`}
      title={stale ? 'Sheet data may be out of date' : 'Synced from the founder’s rating sheet'}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${stale ? 'bg-amber-400' : 'bg-emerald-400/70'}`} />
      Sheet sync · {rel}
    </div>
  )
}

/** Single auth dialog instance — opened by the account panel or "Save route". */
function AuthModalHost() {
  const { authModalOpen, reason, closeAuthModal } = useAuthUi()
  return <AuthModal open={authModalOpen} onClose={closeAuthModal} reason={reason} />
}

const SEARCH_ICON_OFFSET = 36 // pl-9
const SEARCH_PR = 12 // pr-3

/** True when any filter OTHER than the search text is non-default. */
function usePanelFiltersActive(): boolean {
  const filters = useRoadStore((s) => s.filters)
  return (
    filters.bikeTypes.length > 0 ||
    filters.minOverall !== 0 ||
    filters.minFun !== 0 ||
    filters.minPavement !== 0 ||
    filters.minTraffic !== 0 ||
    filters.minDirt !== 0 ||
    filters.minLength !== 0 ||
    filters.maxLength !== 1000000 ||
    filters.fullyPavedOnly ||
    filters.highwaysOnly ||
    !filters.showRated ||
    !filters.showUnrated ||
    filters.region !== null
  )
}

/** Right-aligned "Filters applied" hint. Clipped from the left as the typed
 *  text grows over it, so it disappears letter by letter. */
function FiltersAppliedHint({
  text,
  containerRef,
}: {
  text: string
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const mirrorRef = useRef<HTMLSpanElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const [clip, setClip] = useState(0)
  const filtersActive = usePanelFiltersActive()

  useEffect(() => {
    if (!text) {
      setClip(0)
      return
    }
    const measure = () => {
      const tw = mirrorRef.current?.offsetWidth ?? 0
      const lw = labelRef.current?.offsetWidth ?? 0
      const cw = containerRef.current?.offsetWidth ?? 0
      if (!cw || !lw) return
      const textRight = SEARCH_ICON_OFFSET + tw
      const labelLeft = cw - SEARCH_PR - lw
      setClip(Math.min(lw, Math.max(0, textRight - labelLeft)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [text, containerRef])

  if (!text || !filtersActive) return null
  return (
    <>
      <span
        ref={mirrorRef}
        aria-hidden
        className="absolute invisible whitespace-pre pointer-events-none text-base"
      >
        {text}
      </span>
      <span
        ref={labelRef}
        className="absolute right-3 top-0 h-11 flex items-center text-sm italic text-white/40 pointer-events-none select-none whitespace-nowrap"
        style={{ clipPath: `inset(0 0 0 ${clip}px)` }}
      >
        Filters applied
      </span>
    </>
  )
}

function useRoadMatches(query: string) {
  const roads = useRoadStore((s) => s.roads)
  const filters = useRoadStore((s) => s.filters)
  const q = query.trim().toLowerCase()
  if (q.length < 3) return []
  return filterRoads(roads, { ...filters, search: '' })
    .filter((r) => r.name.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

function SearchSuggestions({
  query,
  open,
  onSelect,
  highlightIndex,
  onHighlight,
}: {
  query: string
  open: boolean
  onSelect: (id: string) => void
  highlightIndex: number
  onHighlight: (i: number) => void
}) {
  const matches = useRoadMatches(query)
  const show = open && matches.length > 0
  const listRef = useRef<HTMLDivElement>(null)
  const pressRef = useRef<{ x: number; y: number; moved: boolean } | null>(null)

  useEffect(() => {
    if (highlightIndex < 0) return
    listRef.current
      ?.querySelector(`[data-idx="${highlightIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex])

  // A touch on a suggestion must not select immediately: the user may be
  // starting a SCROLL of the results list. Track the press and only select
  // on release if the pointer stayed put; once it moves past a small
  // threshold it's a scroll gesture — blur the input so the keyboard
  // retracts and get out of the way.
  const onRowPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // preventDefault keeps the input focused (no blur → 150ms unmount race),
    // which is what previously forced selection onto pointerdown. It does
    // NOT block native touch scrolling of the list.
    e.preventDefault()
    pressRef.current = { x: e.clientX, y: e.clientY, moved: false }
    // ANDROID FIX: this row (and the whole list) can unmount mid-touch the
    // moment we pick. Android Chrome then retargets the leftover synthesized
    // click to whatever is now under the finger — the map — which would run
    // selectRoad(null) and instantly undo the pick. Arm a short click gate so
    // that click is ignored by the map. iOS ends the touch normally, which is
    // why this bug only ever showed on Android.
    suppressNextMapClick()
  }
  const onRowPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current
    if (!press || press.moved) return
    if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > 8) {
      press.moved = true
      const active = document.activeElement as HTMLElement | null
      active?.blur?.()
    }
  }
  const onRowPointerUp = (id: string) => {
    const press = pressRef.current
    pressRef.current = null
    if (press && !press.moved) onSelect(id)
  }
  const onRowPointerCancel = (id: string) => {
    const press = pressRef.current
    pressRef.current = null
    // ANDROID: tapping a suggestion while the keyboard is open often ends in
    // pointercancel instead of pointerup — Chrome takes over the gesture
    // while it dismisses the keyboard and resizes the viewport, so the row
    // never sees pointerup (and the pointerdown preventDefault suppresses
    // the synthesized click). A cancel with NO prior movement IS a tap:
    // honor it, otherwise the pick is silently lost on Android only.
    // A real scroll of the list always moves >8px first (moved=true), so it
    // still correctly refuses to select.
    if (press && !press.moved) onSelect(id)
  }

  if (!show) return null
  return (
    <div
      ref={listRef}
      className="absolute left-0 right-0 top-[calc(100%+6px)] rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-white/25 py-1.5 z-50 overflow-y-auto max-h-[calc(6*2.6rem)]"
    >
      {matches.map((r, i) => (
        <button
          key={r.id}
          data-idx={i}
          // Touch: selection happens on pointerUP, and only if the finger
          // didn't move — otherwise the gesture is a scroll of this list.
          // preventDefault on pointerdown keeps the input focused (the old
          // blur → 150ms unmount race that forced pointerdown selection).
          onPointerDown={onRowPointerDown}
          onPointerMove={onRowPointerMove}
          onPointerUp={() => onRowPointerUp(r.id)}
          onPointerCancel={() => onRowPointerCancel(r.id)}
          onClick={() => onSelect(r.id)}
          onMouseEnter={() => onHighlight(i)}
          className={`w-full text-left px-3 py-2 transition-colors flex items-center justify-between gap-3 ${
            i === highlightIndex ? 'bg-white/10' : 'hover:bg-white/10'
          }`}
        >
          <span className="text-sm text-white flex-1 min-w-0 break-words">
            {r.name}
            {(r.region || r.group) && (
              <span className="block text-white/40 italic">
                ({[r.region, r.group].filter(Boolean).join(' - ')})
              </span>
            )}
          </span>
          {r.ratings.overall != null && (
            <span className="text-xs font-mono text-amber-400 shrink-0">
              {r.ratings.overall.toFixed(1)}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

export default function TopBar({ onFindRide, onToggleFilters, active }: TopBarProps) {
  const filters = useRoadStore((s) => s.filters)
  const setFilters = useRoadStore((s) => s.setFilters)
  const selectRoad = useRoadStore((s) => s.selectRoad)
  const dismissOverlays = useRoadStore((s) => s.dismissOverlays)
  const hoveredId = useRoadStore((s) => s.hoveredId)
  const roads = useRoadStore((s) => s.roads)

  // Ghost hover name — display only, never part of the search value.
  const hoveredRoad = hoveredId ? roads.find((r) => r.id === hoveredId) : null
  const ghostName = filters.search.length === 0 ? hoveredRoad?.name ?? null : null

  const [suggestOpen, setSuggestOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(-1)
  const [mobileSearch, setMobileSearch] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const logoClicks = useRef<number[]>([])
  const desktopSearchBoxRef = useRef<HTMLDivElement>(null)
  const mobileSearchBoxRef = useRef<HTMLDivElement>(null)
  // Mobile: the search bar (open input + closed button) is pinned to the logo
  // button's measured width so the two rows align pixel-for-pixel.
  const logoBtnRef = useRef<HTMLButtonElement>(null)
  const [logoW, setLogoW] = useState(0)
  useEffect(() => {
    const el = logoBtnRef.current
    if (!el) return
    const measure = () => setLogoW(el.offsetWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // The map bumps this counter on any map tap → close the mobile search bar
  // (and its suggestions). Tapping the search bar itself never reaches the
  // map, so this can't close search while the user is using it.
  const searchDismissSeq = useRoadStore((s) => s.searchDismissSeq)
  useEffect(() => {
    if (searchDismissSeq === 0) return
    setMobileSearch(false)
    setSuggestOpen(false)
    setHighlightIdx(-1)
  }, [searchDismissSeq])

  const matches = useRoadMatches(filters.search)

  const pickRoad = (id: string) => {
    selectRoad(id)
    setFilters({ search: '' })
    setSuggestOpen(false)
    setMobileSearch(false)
    setHighlightIdx(-1)
  }

  const handleSearchKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestOpen || matches.length === 0) {
      if (e.key === 'Escape') setSuggestOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx((i) => (i + 1) % matches.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx((i) => (i <= 0 ? matches.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const idx = highlightIdx >= 0 && highlightIdx < matches.length ? highlightIdx : 0
      pickRoad(matches[idx].id)
    } else if (e.key === 'Escape') {
      setSuggestOpen(false)
    }
  }

  const handleLogoClick = () => {
    // Single click: recenter on the stored device location (when available).
    // Quadruple-click (within 1s) still opens the admin portal below.
    const loc = getStoredUserLocation()
    const map = mapInstance.current
    if (loc && map) {
      map.flyTo({ center: [loc.lon, loc.lat], zoom: INITIAL_ZOOM, duration: 900 })
    }
    const now = Date.now()
    const stamps = logoClicks.current.filter((t) => now - t < 1000)
    stamps.push(now)
    logoClicks.current = stamps
    if (stamps.length >= 4) {
      logoClicks.current = []
      dismissOverlays()
      setAdminOpen(true)
    }
  }

  return (
    <div id="rr-topbar" className="absolute top-0 inset-x-0 z-[55] p-3 pointer-events-none">
      <div className="pointer-events-none flex flex-col gap-2">
        {/* Rows are click-transparent so floating map controls (day/night toggle)
            layered at the same height stay tappable; each interactive control
            re-enables pointer events itself. */}
        <div id="rr-topbar-row1" className="pointer-events-auto flex items-center" style={{ gap: 'var(--rr-btn-gap)' }}>
          <button
            ref={logoBtnRef}
            onClick={handleLogoClick}
            className="h-11 shrink-0 flex items-center rounded-lg bg-black/70 backdrop-blur border-2 border-white px-3 cursor-pointer text-left"
          >
            <Logo />
          </button>

          {/* Desktop: search is anchored to the logo at a fixed width. */}
          <div className="relative hidden md:block w-64 xl:w-80 shrink-0">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
            />
            <div className="relative" ref={desktopSearchBoxRef}>
              <input
                value={filters.search}
                onChange={(e) => {
                  setFilters({ search: e.target.value })
                  setSuggestOpen(true)
                  setHighlightIdx(-1)
                }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                onKeyDown={handleSearchKeys}
                placeholder={ghostName ? '' : 'Search roads…'}
                className="w-full h-11 rounded-lg bg-black/70 border-2 border-white backdrop-blur pl-9 pr-3 text-base text-white placeholder:text-white/55 placeholder:italic outline-none"
              />
              {ghostName && (
                <div className="pointer-events-none absolute left-9 top-0 h-11 flex items-center text-lg text-amber-300 truncate max-w-[calc(100%-3rem)]">
                  {ghostName}
                </div>
              )}
              <FiltersAppliedHint
                text={filters.search}
                containerRef={desktopSearchBoxRef}
              />
            </div>
            {suggestOpen && (
              <SearchSuggestions
                query={filters.search}
                open={suggestOpen}
                onSelect={pickRoad}
                highlightIndex={highlightIdx}
                onHighlight={setHighlightIdx}
              />
            )}
          </div>

          <FreshnessHint />

          <div className="hidden md:block flex-1 min-w-0">
            <YourRoutesButton className="w-full justify-center" />
          </div>

          <UnderConstruction
            onOverlayClick={() => window.dispatchEvent(new Event(WELCOME_OPEN_EVENT))}
            className="flex-1 md:flex-none min-w-0"
          >
            <button
              onClick={onFindRide}
              disabled
              title="Coming soon — under construction"
              className="h-11 w-full md:w-auto px-4 md:px-5 rounded-lg bg-amber-400/50 text-black/60 font-semibold text-sm tracking-wide flex items-center justify-center gap-2 cursor-not-allowed"
            >
              <Wand2 size={17} />
              <span className="uppercase hidden sm:inline">Find me a ride</span>
            </button>
          </UnderConstruction>

          <button
            onClick={onToggleFilters}
            className={`relative min-h-11 shrink-0 px-3 md:px-4 rounded-lg border-2 backdrop-blur text-sm flex items-center gap-2 transition-colors ${
              active
                ? 'bg-amber-400/15 border-amber-400/60 text-amber-300'
                : 'bg-black/70 border-white text-white/80'
            }`}
          >
            <SlidersHorizontal size={16} />
            <span className="uppercase tracking-wider text-xs hidden lg:inline">Filters</span>
          </button>

          <div className="shrink-0">
            <AccountPanel />
          </div>
        </div>

        {/* Mobile search row */}
        {/* Click-transparent except its controls: the day/night toggle lives in
            this row's right padding gap and must receive taps. */}
        {/* Right padding = the day/night toggle square (44px) + one goal gap.
            The page margin (0.75rem) is OUTSIDE this row (parent p-3), so the
            toggle left edge lands exactly one goal gap from Your Routes, and
            its right edge aligns with the row 1 Filters button. */}
        <div
          id="rr-topbar-row2"
          className="md:hidden pointer-events-none flex items-center"
          style={{ gap: 'var(--rr-btn-gap)', paddingRight: 'calc(44px + var(--rr-btn-gap))' }}
        >
          {mobileSearch ? (
            <div
              className="relative flex-1 min-w-0 pointer-events-auto"
              ref={mobileSearchBoxRef}
            >
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
              />
              <input
                autoFocus
                value={filters.search}
                onChange={(e) => setFilters({ search: e.target.value })}
                // Blur (keyboard closed by scrolling, or tapping the map) must
                // NOT close search — the search text and results stay visible
                // with the keyboard hidden, and tapping the input again
                // refocuses it and brings the keyboard back. Search closes
                // only via a pick, Escape, or the ✕ button.
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                onFocus={() => setSuggestOpen(true)}
                placeholder="Search roads…"
              className="w-full h-11 rounded-lg bg-black/70 border-2 border-white backdrop-blur pl-9 pr-3 text-base text-white placeholder:text-white/55 placeholder:italic outline-none"
              />
              <FiltersAppliedHint
                text={filters.search}
                containerRef={mobileSearchBoxRef}
              />
              <SearchSuggestions
                query={filters.search}
                open={mobileSearch}
                onSelect={pickRoad}
                highlightIndex={highlightIdx}
                onHighlight={setHighlightIdx}
              />
            </div>
          ) : (
            <button
              // Mobile keyboards resize the viewport and push the road panel
              // over the suggestions, so deselect the road the moment search
              // opens. Mobile-only button (md:hidden) — desktop unaffected.
              onClick={() => {
                selectRoad(null)
                setMobileSearch(true)
              }}
              className="h-11 px-4 shrink-0 rounded-lg bg-black/70 border-2 border-white backdrop-blur flex items-center gap-2 text-sm text-white/60 pointer-events-auto"
              style={{ width: logoW ? `${logoW}px` : undefined }}
            >
              <Search size={16} />
              Search roads…
            </button>
          )}

          {/* Push "Your Routes" flush against the day/night toggle on wider
              phones — otherwise the row packs left and a dead gap opens before
              the toggle (Android only; iPhone overflows so it never showed). */}
          <div className={`pointer-events-auto md:hidden flex-1 min-w-0 ${mobileSearch ? 'hidden' : ''}`}>
            <YourRoutesButton className="w-full justify-center" />
          </div>
        </div>
      </div>

      <AdminPortal open={adminOpen} onClose={() => setAdminOpen(false)} />
      {/* Re-enable pointer events: the root container is pointer-events-none so the
          map stays interactive — dialogs rendered here would otherwise be click-through. */}
      <div className="pointer-events-auto">
        <AuthModalHost />
        <UsernamePrompt />
      </div>
    </div>
  )
}
